import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { RoomCollection } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { PublicUserInfoCollection } from "@lifeat/shared/data-models/user/PublicUserInfoEntity";
import StackedButton from "@common/LifeAtButton/Variants/StackedButton";
import useDbValueListener from "@hooks/useDbValueListener";
import LinkIcon from "@icons/LinkIcon.svg";
import CalendarWidgetToolbarIcon from "@icons/WidgetToolbar/Calendar-ToolbarWidget-Icon.svg";
import InstagramIcon from "@resources/logos/social/Instagram.svg";
import TikTokIcon from "@resources/logos/social/TikTok.svg";
import TwitterIcon from "@resources/logos/social/Twitter.svg";
import YoutubeIcon from "@resources/logos/social/Youtube.svg";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import UserAvatar from "@user/components/UserAvatar/UserAvatar";
import { getPublicUserInfo } from "@user/service/UserService";
import { BrowserUtil } from "@util/index";
import { useRoomContext } from "../../RoomContext";
import RoomMemberDisplayName from "../../RoomMemberDisplayName";
import { PublicUserInfo, RoomParams } from "../../RoomTypes";

const CardContainer = styled.div`
    width: 100%;
    display: flex;
    padding: 10px;
`;

const UserAviContainer = styled.div`
    height: 100%;
    width: fit-content;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 10px;
    overflow: hidden;
    gap: 8px;
`;

const SocialLinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MemberCountContainer = styled.div`
    display: flex;
    cursor: pointer;
    gap: 3px;
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorPrimary};
    font-weight: 500;
`;

const MemberCount = styled.div`
    font-weight: 530;
`;

const UpgradeButton = styled(StackedButton)`
    width: fit-content;
    margin: 0;
`;

const IconLink = styled.div`
    display: flex;
    cursor: pointer;
`;

const StyledCalendarIcon = styled(CalendarWidgetToolbarIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
    transform: scale(0.9);
`;

const StyledInstagramIcon = styled(InstagramIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledYoutubeIcon = styled(YoutubeIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledTwitterIcon = styled(TwitterIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledTikTokIcon = styled(TikTokIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const StyledLinkIcon = styled(LinkIcon)`
    fill: ${({ theme }) => theme.textColorPrimary};
`;

const RoomDescription = styled.div`
    display: flex;
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorPrimary};
    word-break: break-word;
    overflow-y: scroll;
    max-height: 75px;

    ${ScrollbarStyles};
    scrollbar-gutter: stable both-edges;
`;

const AFFILIATE_UPGRADE_BUTTON_LABEL = "Subscribe to LifeAt Pro";

interface RoomChatCardProps {
    readonly roomOwnerUid: string;
}
export default function RoomChatCard(props: RoomChatCardProps) {
    const { roomId }: RoomParams = useParams();
    const { totalMemberCount, isMembersModalOpen, setIsMembersModalOpen } = useRoomContext();

    const isMemberCountDisplayed = useDbValueListener(
        RoomCollection.at(roomId).at("roomDisplayInfo").at("displayMemberCount").node,
    );

    const roomDescription = useDbValueListener(
        RoomCollection.at(roomId).at("roomDisplayInfo").at("roomDescription").node,
    );

    const publicUserInfoNode = PublicUserInfoCollection.at(props.roomOwnerUid).at("publicUserLinks");
    const publicUserLinks = useDbValueListener(publicUserInfoNode.node);

    const [roomHostDisplayInfo, setRoomHostDisplayInfo] = useState<PublicUserInfo>();

    useEffect(() => {
        if (props.roomOwnerUid) {
            getPublicUserInfo(props.roomOwnerUid).then((publicUserInfo: PublicUserInfo) => {
                setRoomHostDisplayInfo(publicUserInfo);
            });
        }
    }, [props.roomOwnerUid]);

    const linkButtons = (
        <SocialLinkRow>
            {publicUserLinks?.calendarLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.calendarLink ?? "")}>
                    <StyledCalendarIcon />
                </IconLink>
            )}
            {publicUserLinks?.instagramLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.instagramLink ?? "")}>
                    <StyledInstagramIcon />
                </IconLink>
            )}
            {publicUserLinks?.youtubeLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.youtubeLink ?? "")}>
                    <StyledYoutubeIcon />
                </IconLink>
            )}
            {publicUserLinks?.tiktokLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.tiktokLink ?? "")}>
                    <StyledTikTokIcon />
                </IconLink>
            )}
            {publicUserLinks?.twitterLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.twitterLink ?? "")}>
                    <StyledTwitterIcon />
                </IconLink>
            )}
            {publicUserLinks?.websiteLink && (
                <IconLink onClick={() => BrowserUtil.openLinkNewTab(publicUserLinks.websiteLink ?? "")}>
                    <StyledLinkIcon />
                </IconLink>
            )}
        </SocialLinkRow>
    );

    return (
        <CardContainer>
            <UserAviContainer>
                <UserAvatar
                    size={"small"}
                    userDisplayName={roomHostDisplayInfo?.userDisplayName ?? "User"}
                    userAviUrl={roomHostDisplayInfo?.userAviUrl ?? ""}
                />
            </UserAviContainer>

            <Content>
                <RoomMemberDisplayName
                    displayName={roomHostDisplayInfo?.userDisplayName}
                    isDisplayNameBold={true}
                    isHost={true}
                />
                {linkButtons}
                {isMemberCountDisplayed && (
                    <MemberCountContainer onClick={() => setIsMembersModalOpen(!isMembersModalOpen)}>
                        <MemberCount>{formatMemberCount(totalMemberCount)}</MemberCount>
                        members
                    </MemberCountContainer>
                )}
                {publicUserLinks?.referralLink && (
                    <UpgradeButton
                        label={AFFILIATE_UPGRADE_BUTTON_LABEL}
                        border={false}
                        variant={"blue"}
                        action={"link"}
                        link={publicUserLinks.referralLink}
                    />
                )}
                <RoomDescription>{roomDescription}</RoomDescription>
            </Content>
        </CardContainer>
    );
}

/**
 * Formats number into condensed string
 * Ex: 20000 -> "20k"
 * @param members
 */
function formatMemberCount(members: number) {
    return Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(members);
}
